import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Competition,
  CompetitionInput,
  CompetitionRoutineInput,
  ItineraryInput,
  PlacementInput,
} from '@pirouette/shared';
import {
  competitionInputSchema,
  competitionRoutineInputSchema,
  itineraryInputSchema,
  placementInputSchema,
} from '@pirouette/shared';
import type { Database } from '@pirouette/db';
import { supabase } from '@/lib/supabase';
import { stripUndefined } from '@/lib/db';
import { env } from '@/env';

type RpcReturns<K extends keyof Database['public']['Functions']> =
  Database['public']['Functions'][K]['Returns'];

// COMPETITIONS -------------------------------------------------------------

export function useCompetitions() {
  return useQuery({
    queryKey: ['competitions'],
    queryFn: async (): Promise<Competition[]> => {
      const { data, error } = await supabase
        .from('competitions')
        .select('*')
        .is('deleted_at', null)
        .order('start_date', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useCompetition(id: string | undefined) {
  return useQuery({
    enabled: !!id,
    queryKey: ['competition', id],
    queryFn: async (): Promise<Competition | null> => {
      const { data, error } = await supabase
        .from('competitions')
        .select('*')
        .eq('id', id!)
        .is('deleted_at', null)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });
}

export function useCreateCompetition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CompetitionInput): Promise<Competition> => {
      const parsed = competitionInputSchema.parse(input);
      const { data, error } = await supabase
        .from('competitions')
        .insert(stripUndefined(parsed))
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['competitions'] });
    },
  });
}

// ROUTINES -----------------------------------------------------------------

export interface CompetitionRoutineRow {
  id: string;
  competition_id: string;
  dance_id: string;
  division: string | null;
  level: string | null;
  entry_fee_cents: number;
  competition_entry_number: string | null;
  scheduled_at: string | null;
  dances: { id: string; name: string; style: string; length_seconds: number } | null;
}

export function useCompetitionRoutines(competitionId: string | undefined) {
  return useQuery({
    enabled: !!competitionId,
    queryKey: ['comp-routines', competitionId],
    queryFn: async (): Promise<CompetitionRoutineRow[]> => {
      const { data, error } = await supabase
        .from('competition_routines')
        .select('*, dances(id, name, style, length_seconds)')
        .eq('competition_id', competitionId!)
        .order('scheduled_at', { ascending: true, nullsFirst: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useAddCompetitionRoutine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CompetitionRoutineInput) => {
      const parsed = competitionRoutineInputSchema.parse(input);
      const { error } = await supabase.from('competition_routines').insert(stripUndefined(parsed));
      if (error) throw error;
    },
    onSuccess: (_, input) => {
      void qc.invalidateQueries({ queryKey: ['comp-routines', input.competition_id] });
    },
  });
}

// ITINERARY ----------------------------------------------------------------

export interface ItineraryRow {
  id: string;
  competition_id: string;
  kind: 'call' | 'travel' | 'hotel' | 'meal' | 'rehearsal' | 'other';
  label: string;
  start_at: string;
  end_at: string | null;
  location: string | null;
  notes: string | null;
}

export function useItinerary(competitionId: string | undefined) {
  return useQuery({
    enabled: !!competitionId,
    queryKey: ['itinerary', competitionId],
    queryFn: async (): Promise<ItineraryRow[]> => {
      const { data, error } = await supabase
        .from('competition_itineraries')
        .select('*')
        .eq('competition_id', competitionId!)
        .order('start_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useAddItineraryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ItineraryInput) => {
      const parsed = itineraryInputSchema.parse(input);
      const { error } = await supabase
        .from('competition_itineraries')
        .insert(stripUndefined(parsed));
      if (error) throw error;
    },
    onSuccess: (_, input) => {
      void qc.invalidateQueries({ queryKey: ['itinerary', input.competition_id] });
    },
  });
}

export function useRemoveItineraryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; competition_id: string }) => {
      const { error } = await supabase.from('competition_itineraries').delete().eq('id', args.id);
      if (error) throw error;
    },
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: ['itinerary', args.competition_id] });
    },
  });
}

// PLACEMENTS ---------------------------------------------------------------

export interface PlacementRow {
  id: string;
  competition_routine_id: string;
  placement: number | null;
  total_competitors: number | null;
  score: number | null;
  award: string | null;
  notes: string | null;
  awarded_at: string;
}

export function usePlacements(competitionId: string | undefined) {
  return useQuery({
    enabled: !!competitionId,
    queryKey: ['placements', competitionId],
    queryFn: async (): Promise<PlacementRow[]> => {
      // Join through competition_routines so we filter by competition.
      const { data, error } = await supabase
        .from('routine_placements')
        .select(
          'id, competition_routine_id, placement, total_competitors, score, award, notes, awarded_at, competition_routines!inner(competition_id)',
        )
        .eq('competition_routines.competition_id', competitionId!);
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useUpsertPlacement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { input: PlacementInput; competition_id: string }) => {
      const parsed = placementInputSchema.parse(args.input);
      const { error } = await supabase
        .from('routine_placements')
        .upsert(stripUndefined(parsed), { onConflict: 'competition_routine_id' });
      if (error) throw error;
    },
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: ['placements', args.competition_id] });
    },
  });
}

// MEDIA SIGNING ------------------------------------------------------------
// Fetches a 1h presigned R2 URL through the Edge Function. The function
// enforces RLS by calling the request_media_signed_url RPC as the user.

export function useSignedMediaUrl(assetId: string | undefined) {
  return useQuery({
    enabled: !!assetId,
    // Signed URLs are short-lived; refetch on focus to keep them fresh.
    staleTime: 30 * 60 * 1000,
    queryKey: ['signed-url', assetId],
    queryFn: async (): Promise<string> => {
      const session = (await supabase.auth.getSession()).data.session;
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');
      const fnUrl = `${env.VITE_SUPABASE_URL}/functions/v1/sign-media-url`;
      const res = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({ asset_id: assetId }),
      });
      if (!res.ok) {
        // Don't echo the raw response body — Edge Function errors may
        // include internal detail. Parse the JSON error field if present,
        // otherwise surface only the status code.
        let detail = `status ${res.status}`;
        try {
          const errJson = (await res.json()) as { error?: string };
          if (errJson.error) detail = errJson.error;
        } catch {
          // fall back to the status code
        }
        throw new Error(`Could not load the media: ${detail}`);
      }
      const json = (await res.json()) as { url: string; expires_in: number };
      return json.url;
    },
  });
}

// COMMAND CENTER -----------------------------------------------------------
//
// Mirrors @/data/recital.ts: a single `jsonb` summary RPC narrowed at the read
// boundary, a typed per-row detail RPC, and an admin-only write. Nothing here
// bypasses RLS — each call runs as the authenticated admin and the per-table
// policies scope it to the caller's studio (tenant).

/**
 * Summary rollup for one competition. `competition_command_center` returns a
 * single `jsonb` object (generated type `Json`), so we model the contract
 * explicitly and narrow the payload below. Every field is computed server-side
 * from whatever data exists, so treat each as possibly absent.
 */
export interface CompetitionCommandCenter {
  entries: number;
  total_fees_cents: number;
  scheduled: number;
  itinerary_items: number;
  results_recorded: number;
  dancers: number;
}

/** One routine/result row for the command-center table. */
export type CompetitionResultDetailRow = RpcReturns<'competition_results_detail'>[number];

/** Admin write into competition_results (placement / score / award per category). */
export interface RecordResultInput {
  competition_routine_id: string;
  /** Defaults to 'overall' server-side; the UI records overall results. */
  category?: string;
  placement?: number | null;
  score?: number | null;
  award?: string | null;
  notes?: string | null;
}

/** Command-center summary rollup for one competition (jsonb RPC). */
export function useCompetitionCommandCenter(competitionId: string | undefined) {
  return useQuery({
    enabled: !!competitionId,
    queryKey: ['competition', 'command-center', competitionId],
    queryFn: async (): Promise<CompetitionCommandCenter> => {
      const { data, error } = await supabase.rpc('competition_command_center', {
        p_competition_id: competitionId!,
      });
      if (error) throw error;
      return narrowCommandCenter(data);
    },
  });
}

/** Per-routine results detail (routine, division/level, schedule, placement…). */
export function useCompetitionResultsDetail(competitionId: string | undefined) {
  return useQuery({
    enabled: !!competitionId,
    queryKey: ['competition', 'results-detail', competitionId],
    queryFn: async (): Promise<CompetitionResultDetailRow[]> => {
      const { data, error } = await supabase.rpc('competition_results_detail', {
        p_competition_id: competitionId!,
      });
      if (error) throw error;
      return data ?? [];
    },
  });
}

/**
 * Record (upsert) an overall result for a routine. Conflict target matches the
 * table's unique key `(studio_id, competition_routine_id, category)`; `studio_id`
 * is left to the row default so RLS scopes it to the caller's studio.
 */
export function useRecordResult() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { input: RecordResultInput; competition_id: string }) => {
      const { error } = await supabase.from('competition_results').upsert(
        stripUndefined({
          competition_routine_id: args.input.competition_routine_id,
          category: args.input.category ?? 'overall',
          placement: args.input.placement ?? null,
          score: args.input.score ?? null,
          award: args.input.award ?? null,
          notes: args.input.notes ?? null,
        }),
        { onConflict: 'studio_id,competition_routine_id,category' },
      );
      if (error) throw error;
    },
    onSuccess: (_, args) => {
      void qc.invalidateQueries({
        queryKey: ['competition', 'command-center', args.competition_id],
      });
      void qc.invalidateQueries({
        queryKey: ['competition', 'results-detail', args.competition_id],
      });
    },
  });
}

// Helpers ------------------------------------------------------------------

function asCcNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** Narrow the `Json` command-center payload into typed counts. */
function narrowCommandCenter(value: unknown): CompetitionCommandCenter {
  const obj = (value ?? {}) as Record<string, unknown>;
  return {
    entries: asCcNumber(obj.entries),
    total_fees_cents: asCcNumber(obj.total_fees_cents),
    scheduled: asCcNumber(obj.scheduled),
    itinerary_items: asCcNumber(obj.itinerary_items),
    results_recorded: asCcNumber(obj.results_recorded),
    dancers: asCcNumber(obj.dancers),
  };
}
